/**
 * The start/stop/adjust logic behind every auto-scroll control.
 *
 * AutoScrollPlugin (full and compact) and SuperScroll had near-identical copies of this; adding a
 * second mode to each would have made three. They all consume this instead.
 *
 * In constant mode "adjust" steps through the seven speed levels. In smart mode the levels don't
 * apply — the schedule sets the velocity — so it nudges the pace multiplier, which is the one knob
 * over the computed schedule. Pace is saved per song and lives in localStorage, because it depends
 * on the screen the song is being read from.
 */

import { useCallback, useEffect, useState } from 'react';

import { useGlobalContext } from '../context/GlobalContext';
import { DEFAULT_PACE, PACE_MAX, PACE_MIN, PACE_STEP, SPEED_LEVELS, clampPace } from '../utils/autoScrollConstants';
import { getScheduleTime, setScrollPace, startScroll, stopScroll } from '../utils/autoScrollHelpers';
import { getSchedule } from '../utils/smartScrollSchedule';
import {
  getScrollPaceForSong,
  getScrollSpeedForSong,
  saveScrollPaceForSong,
  saveScrollSpeedForSong,
} from '../utils/scrollSpeedPreferences';
import { getSetting, settingKeys } from '../utils/settings';
import { SMART_SCROLL_CHANGED_EVENT, isSmartScrollEnabled } from './useSmartScrollSchedule';

// How far from the song's average velocity counts as visibly quicker or slower. Wide enough that
// the indicator sits still through the ordinary drift of a verse rather than flickering.
const TREND_BAND = 0.15;

// The indicator is a reassurance, not an instrument — a few reads a second is plenty, and it keeps
// this off the animation frame entirely.
const TREND_POLL_MS = 300;

// The caret pulses, and how urgently says how far off the average this part runs: a gentle breath
// for a slight lean, a quick blink for a chorus at half speed. Measured multiplicatively, so twice
// as fast and half as fast pulse alike.
const TREND_PULSE_SLOWEST_MS = 1800;
const TREND_PULSE_FASTEST_MS = 450;
// Twice (or half) the average is where the pulse tops out
const TREND_PULSE_FULL_DEVIATION = 1;
// Quantised, so the rate settles on a value and stays there instead of drifting every poll
const TREND_PULSE_STEP_MS = 150;

const pulseForRatio = (ratio) => {
  if (!(ratio > 0)) return TREND_PULSE_SLOWEST_MS;

  const deviation = Math.max(ratio, 1 / ratio) - 1;
  const reach = Math.min(1, Math.max(0, (deviation - TREND_BAND) / (TREND_PULSE_FULL_DEVIATION - TREND_BAND)));
  const raw = TREND_PULSE_SLOWEST_MS - reach * (TREND_PULSE_SLOWEST_MS - TREND_PULSE_FASTEST_MS);

  return Math.round(raw / TREND_PULSE_STEP_MS) * TREND_PULSE_STEP_MS;
};

const shouldRememberSpeed = () => {
  const stored = getSetting(settingKeys.rememberScrollSpeed);
  return stored !== undefined ? stored : true; // Default to true
};

const useAutoScrollControls = (song) => {
  const {
    scrollTimerRef,
    isScrolling,
    setIsScrolling,
    scrollingSpeed,
    setScrollingSpeed,
    scrollingPace: pace,
    setScrollingPace,
  } = useGlobalContext();
  const [smart, setSmart] = useState(isSmartScrollEnabled);
  const [savedSpeed, setSavedSpeed] = useState(null);
  const [trend, setTrend] = useState('steady');
  const [trendPulse, setTrendPulse] = useState(TREND_PULSE_SLOWEST_MS);

  useEffect(() => {
    const syncSmart = () => setSmart(isSmartScrollEnabled());
    window.addEventListener(SMART_SCROLL_CHANGED_EVENT, syncSmart);
    return () => window.removeEventListener(SMART_SCROLL_CHANGED_EVENT, syncSmart);
  }, []);

  // Restore this song's saved pace and hand it to the engine, which reads it live.
  // Remember Speed covers the pace too — it's the same promise, just a different number behind it
  // in smart mode, so one toggle governs both rather than leaving a dead switch in the modal.
  //
  // Both reads happen here, on song change, rather than during render: preferences are unbounded
  // now, and reading one parses the whole map. Once per song is fine; once per render is not.
  useEffect(() => {
    const remember = Boolean(song?._id) && shouldRememberSpeed();

    const savedPace = remember ? getScrollPaceForSong(song._id) : null;
    const restored = savedPace !== null ? clampPace(savedPace) : DEFAULT_PACE;
    setScrollingPace(restored);
    setScrollPace(restored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?._id]);

  // Re-read the saved level whenever one is chosen anywhere, so the corner marker on the plugin's
  // buttons follows a change made from the pinned bar or the overlay
  useEffect(() => {
    setSavedSpeed(song?._id && shouldRememberSpeed() ? getScrollSpeedForSong(song._id) : null);
  }, [song?._id, scrollingSpeed]);

  // Whether the schedule is currently running quicker or slower than this song's average. Smart
  // scroll changes speed by itself, so without this there's no way to tell it apart from drift.
  // Measured against the average rather than as a rate of change: velocity is near-constant within
  // a part and moves only at the boundaries, so a derivative would read zero almost all the time.
  useEffect(() => {
    if (!smart || !isScrolling) {
      setTrend('steady');
      return undefined;
    }

    const read = () => {
      const schedule = getSchedule();
      if (!schedule || !schedule.averageVelocity) return;

      const ratio = schedule.velocityAt(getScheduleTime()) / schedule.averageVelocity;
      // Setting the same value again is a no-op in React, so a steady stretch costs no renders
      setTrend(ratio > 1 + TREND_BAND ? 'faster' : ratio < 1 - TREND_BAND ? 'slower' : 'steady');
      setTrendPulse(pulseForRatio(ratio));
    };

    read();
    const timer = setInterval(read, TREND_POLL_MS);
    return () => clearInterval(timer);
  }, [smart, isScrolling]);

  const stop = useCallback(() => stopScroll(scrollTimerRef, setIsScrolling), [scrollTimerRef, setIsScrolling]);

  const scrollAtSpeed = useCallback(
    (speed) => {
      // Re-clicking the speed that's already running is how you stop
      if (isScrolling && speed === scrollingSpeed) {
        stop();
        return;
      }

      // The effect above picks the new value back up, in this instance and the others
      if (song?._id && shouldRememberSpeed()) saveScrollSpeedForSong(song._id, speed);

      startScroll(scrollTimerRef, setIsScrolling, setScrollingSpeed, speed, stop);
    },
    [isScrolling, scrollingSpeed, song?._id, scrollTimerRef, setIsScrolling, setScrollingSpeed, stop],
  );

  const toggle = useCallback(() => {
    if (isScrolling) stop();
    else startScroll(scrollTimerRef, setIsScrolling, setScrollingSpeed, scrollingSpeed, stop);
  }, [isScrolling, scrollingSpeed, scrollTimerRef, setIsScrolling, setScrollingSpeed, stop]);

  const changePace = useCallback(
    (next) => {
      const clamped = clampPace(next);
      setScrollingPace(clamped);
      // Straight to the engine, so a nudge lands on the next frame instead of restarting the scroll
      setScrollPace(clamped);
      if (song?._id && shouldRememberSpeed()) saveScrollPaceForSong(song._id, clamped);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [song?._id],
  );

  const speedIndex = SPEED_LEVELS.findIndex((level) => level.speed === scrollingSpeed);

  /* -------------------------------------------------------------------------
   * The two modes, behind one interface: a label, a pair of bounds and a pair
   * of nudges, so the controls don't branch on the mode themselves.
   * ---------------------------------------------------------------------- */

  if (smart) {
    return {
      smart,
      isScrolling,
      scrollingSpeed,
      trend,
      trendPulse,
      pace,
      changePace,
      stop,
      toggle,
      // A percentage reads at a glance and fits the same slot as the metronome's BPM; "1.05x"
      // needs two decimals to not be a lie, and that's more than a control you tap should carry
      label: `${Math.round(pace * 100)}%`,
      canSlowDown: pace > PACE_MIN,
      canSpeedUp: pace < PACE_MAX,
      slowDown: () => changePace(pace - PACE_STEP),
      speedUp: () => changePace(pace + PACE_STEP),
    };
  }

  return {
    smart,
    isScrolling,
    scrollingSpeed,
    trend: 'steady', // Constant mode has one speed, so there is nothing to report
    trendPulse: TREND_PULSE_SLOWEST_MS,
    pace,
    changePace,
    stop,
    toggle,
    scrollAtSpeed,
    savedSpeed,
    label: speedIndex !== -1 ? SPEED_LEVELS[speedIndex].label : '4',
    canSlowDown: speedIndex > 0,
    canSpeedUp: speedIndex !== -1 && speedIndex < SPEED_LEVELS.length - 1,
    slowDown: () => speedIndex > 0 && scrollAtSpeed(SPEED_LEVELS[speedIndex - 1].speed),
    speedUp: () =>
      speedIndex !== -1 && speedIndex < SPEED_LEVELS.length - 1 && scrollAtSpeed(SPEED_LEVELS[speedIndex + 1].speed),
  };
};

export default useAutoScrollControls;
